import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { Send, Webhook, X, ChevronLeft, ChevronRight, Volume2, VolumeX, Bell, Music2, DoorOpen, Palette } from "lucide-react";
import { useSoundSettings } from "@/hooks/useSoundSettings";
import { usePulseSettings } from "@/hooks/usePulseSettings";
import { usePulseColors } from "@/hooks/usePulseColors";

export default function SettingsDialog({ open, onOpenChange, onPrevious, onNext, mode }) {
  const [webhookUrl, setWebhookUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const {
    soundEnabled,
    soundVolume,
    soundPitch,
    setSoundEnabled,
    setSoundVolume,
    setSoundPitch,
  } = useSoundSettings();

  const {
    pulseSpeed,
    pulseIntensity,
    setPulseSpeed,
    setPulseIntensity,
  } = usePulseSettings();

  const {
    closingColor,
    openingColor,
    setClosingColor,
    setOpeningColor,
    colorPalette,
    colorPaletteWithGreen,
  } = usePulseColors();

  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open) {
      if (!dialog.open) dialog.showModal();
    } else {
      if (dialog.open) dialog.close();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get("/settings")
      .then(({ data }) => {
        setWebhookUrl(data.discord_webhook_url || "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const handleSaveWebhook = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { discord_webhook_url: webhookUrl.trim() });
      setWebhookUrl(data.discord_webhook_url || "");
    } catch (e) {
      // silently ignore
    } finally {
      setSaving(false);
    }
  };

  const handleSendDiscord = async () => {
    setSending(true);
    try {
      await api.post("/discord/send");
    } catch (e) {
      // silently ignore
    } finally {
      setSending(false);
    }
  };


  const isPrivate = mode === "private";
  const accentBorder = isPrivate ? "border-emerald-500/30" : "border-orange-500/30";
  const accentLabel = isPrivate ? "text-emerald-400" : "text-orange-400";
  const accentNavHover = isPrivate ? "hover:border-emerald-500 hover:text-emerald-400" : "hover:border-orange-500 hover:text-orange-400";
  const accentIcon = isPrivate ? "text-emerald-400" : "text-orange-400";
  const accentFocusBorder = isPrivate ? "focus:border-emerald-500" : "focus:border-orange-500";
  const accentFocusRing = isPrivate ? "focus-visible:ring-emerald-500" : "focus-visible:ring-orange-500";
  const accentSendBtn = isPrivate
    ? "border-emerald-500 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500 hover:text-black disabled:hover:bg-emerald-500/10 disabled:hover:text-emerald-400"
    : "border-orange-500 bg-orange-500/10 text-orange-400 hover:bg-orange-500 hover:text-black disabled:hover:bg-orange-500/10 disabled:hover:text-orange-400";
  const accentLoading = isPrivate ? "text-emerald-400" : "text-orange-400";

  return (
    <dialog
      ref={dialogRef}
      onClose={() => onOpenChange(false)}
      className={`backdrop:bg-black/80 bg-[#0B0E14]/95 border ${accentBorder} rounded-none text-white w-full max-w-lg p-4 max-h-[90vh] overflow-y-auto`}
      data-testid="settings-dialog"
    >
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <div className={`text-[11px] tracking-[0.3em] uppercase ${accentLabel} mb-1`}>Operator Settings</div>
          <div className="font-display text-2xl tracking-tight">Settings</div>
          <div className="text-slate-400 text-sm mt-1">
            Manage notifications and push board updates to Discord.
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {onPrevious && (
            <button
              type="button"
              onClick={onPrevious}
              aria-label="Previous panel"
              title="Who's Online"
              className={`flex items-center justify-center w-7 h-7 border border-slate-700 text-slate-400 ${accentNavHover} transition-colors`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          {onNext && (
            <button
              type="button"
              onClick={onNext}
              aria-label="Next panel"
              title="Who's Online"
              className={`flex items-center justify-center w-7 h-7 border border-slate-700 text-slate-400 ${accentNavHover} transition-colors`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className={`flex items-center justify-center w-7 h-7 border border-slate-700 text-slate-400 ${accentNavHover} transition-colors`}
            aria-label="Close settings"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

        {loading ? (
          <div className={`${accentLoading} text-xs tracking-widest uppercase animate-pulse py-4 text-center`}>
            Loading…
          </div>
        ) : (
          <div className="space-y-4 mt-1">
            {/* Discord */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <Webhook className={`w-3.5 h-3.5 ${accentIcon}`} />
                <h3 className="text-[11px] tracking-[0.3em] uppercase text-slate-300">Discord Webhook</h3>
              </div>
              <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                Paste a Discord webhook URL (Server Settings → Integrations → Webhooks). Click
                "Send Now" to post your current board to that channel.
              </p>
              <Label className="text-[11px] tracking-[0.3em] uppercase text-slate-400">Webhook URL</Label>
              <Input
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://discord.com/api/webhooks/…"
                className={`rounded-none bg-[#121822] border-slate-800 ${accentFocusBorder} focus-visible:ring-1 ${accentFocusRing} text-white mt-1.5`}
                data-testid="discord-webhook-input"
              />
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={handleSaveWebhook}
                  disabled={saving}
                  data-testid="save-webhook"
                  className={`px-4 py-2 border border-slate-700 text-slate-300 ${accentNavHover} transition-colors text-xs tracking-widest uppercase disabled:opacity-40`}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={handleSendDiscord}
                  disabled={sending || !webhookUrl}
                  data-testid="send-to-discord"
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 border ${accentSendBtn} transition-colors text-xs tracking-widest uppercase disabled:opacity-40`}
                >
                  <Send className="w-3.5 h-3.5" />
                  {sending ? "Sending…" : "Send Now"}
                </button>
              </div>
            </section>

            {/* Divider */}
            <div className="border-t border-slate-800/60" />

            {/* Sound Alerts */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <Bell className={`w-3.5 h-3.5 ${accentIcon}`} />
                <h3 className="text-[11px] tracking-[0.3em] uppercase text-slate-300">Sound Alerts</h3>
              </div>
              <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                Play an audio tone when a card cooldown expires. Adjust the volume to your
                preference.
              </p>

              {/* Toggle */}
              <div className="flex items-center justify-between mb-3">
                <Label
                  htmlFor="sound-toggle"
                  className="text-[11px] tracking-[0.3em] uppercase text-slate-400 cursor-pointer select-none"
                >
                  Alert Sound
                </Label>
                <button
                  id="sound-toggle"
                  type="button"
                  role="switch"
                  aria-checked={soundEnabled}
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  data-testid="sound-toggle"
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 ${accentFocusRing} focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0E14] ${
                    soundEnabled
                      ? isPrivate ? "border-emerald-500/60 bg-emerald-500/20" : "border-orange-500/60 bg-orange-500/20"
                      : "border-slate-700 bg-slate-800/60"
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-3.5 w-3.5 rounded-full shadow-sm transition-transform ${
                      soundEnabled
                        ? "translate-x-[18px] bg-white"
                        : "translate-x-[2px] bg-slate-500"
                    }`}
                  />
                </button>
              </div>

              {/* Volume */}
              <div className={`mb-3 transition-opacity ${soundEnabled ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[11px] tracking-[0.3em] uppercase text-slate-400 flex items-center gap-1.5">
                    {soundVolume === 0 ? (
                      <VolumeX className="w-3 h-3 text-slate-500" />
                    ) : (
                      <Volume2 className={`w-3 h-3 ${accentIcon}`} />
                    )}
                    Volume
                  </Label>
                  <span className={`text-xs font-mono ${accentIcon} tabular-nums`}>{soundVolume}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={soundVolume}
                  onChange={(e) => setSoundVolume(Number(e.target.value))}
                  data-testid="sound-volume-slider"
                  disabled={!soundEnabled}
                  className="w-full h-1.5 appearance-none rounded-full cursor-pointer bg-slate-800 disabled:cursor-not-allowed"
                  style={{ accentColor: isPrivate ? "rgb(52 211 153)" : "rgb(251 146 60)" }}
                />
                <div className="flex justify-between mt-1">
                  <span className="text-[10px] text-slate-600 tracking-widest">0%</span>
                  <span className="text-[10px] text-slate-600 tracking-widest">100%</span>
                </div>
              </div>

              {/* Pitch */}
              <div className={`transition-opacity ${soundEnabled ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[11px] tracking-[0.3em] uppercase text-slate-400 flex items-center gap-1.5">
                    <Music2 className={`w-3 h-3 ${accentIcon}`} />
                    Pitch
                  </Label>
                  <span className={`text-xs font-mono ${accentIcon} tabular-nums`}>{soundPitch}Hz</span>
                </div>
                <input
                  type="range"
                  min="440"
                  max="1320"
                  step="40"
                  value={soundPitch}
                  onChange={(e) => setSoundPitch(Number(e.target.value))}
                  data-testid="sound-pitch-slider"
                  disabled={!soundEnabled}
                  className="w-full h-1.5 appearance-none rounded-full cursor-pointer bg-slate-800 disabled:cursor-not-allowed"
                  style={{ accentColor: isPrivate ? "rgb(52 211 153)" : "rgb(251 146 60)" }}
                />
                <div className="flex justify-between mt-1">
                  <span className="text-[10px] text-slate-600 tracking-widest">440Hz</span>
                  <span className="text-[10px] text-slate-600 tracking-widest">1320Hz</span>
                </div>
              </div>

            </section>

            {/* Pulse Animations */}
            <section>
              <div className="h-px bg-slate-800 mb-3" />
              <div className="flex items-center gap-2 mb-3">
                <DoorOpen className={`w-3.5 h-3.5 ${accentIcon}`} />
                <h3 className="text-[11px] tracking-[0.3em] uppercase text-slate-300">Vault Door Pulse</h3>
              </div>

              {/* Vault pulse speed */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-[11px] tracking-widest uppercase text-slate-400">Speed</Label>
                  <span className={`text-[11px] font-mono ${accentIcon}`}>{pulseSpeed.toFixed(1)}s</span>
                </div>
                <p className="text-[11px] text-slate-600 mb-2">Slow ← animation speed → Fast</p>
                <input
                  type="range"
                  min="0.1"
                  max="5.0"
                  step="0.1"
                  value={pulseSpeed}
                  onChange={(e) => setPulseSpeed(parseFloat(e.target.value))}
                  data-testid="vault-pulse-speed-slider"
                  className="w-full h-1.5 appearance-none bg-slate-800 rounded-none cursor-pointer"
                  style={{ accentColor: isPrivate ? "rgb(52 211 153)" : "rgb(251 146 60)" }}
                />
              </div>

              {/* Vault pulse intensity */}
              <div className="mb-1">
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-[11px] tracking-widest uppercase text-slate-400">Intensity</Label>
                  <span className={`text-[11px] font-mono ${accentIcon}`}>{Math.round(pulseIntensity * 100)}%</span>
                </div>
                <p className="text-[11px] text-slate-600 mb-2">Dim ← glow intensity → Bright</p>
                <input
                  type="range"
                  min="0.1"
                  max="1.0"
                  step="0.1"
                  value={pulseIntensity}
                  onChange={(e) => setPulseIntensity(parseFloat(e.target.value))}
                  data-testid="vault-pulse-intensity-slider"
                  className="w-full h-1.5 appearance-none bg-slate-800 rounded-none cursor-pointer"
                  style={{ accentColor: isPrivate ? "rgb(52 211 153)" : "rgb(251 146 60)" }}
                />
              </div>
            </section>

            {/* Vault Door Pulse Colors */}
            <section>
              <div className="h-px bg-slate-800 mb-3" />
              <div className="flex items-center gap-2 mb-2">
                <Palette className={`w-3.5 h-3.5 ${accentIcon}`} />
                <h3 className="text-[11px] tracking-[0.3em] uppercase text-slate-300">Vault Door Pulse Colors</h3>
              </div>
              <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                Choose pulse colors you can clearly distinguish. Useful if you have difficulty
                differentiating red and green.
              </p>

              {/* Closing color */}
              <div className="mb-3">
                <Label className="text-[11px] tracking-widest uppercase text-slate-400 block mb-1.5">
                  Closing Color
                </Label>
                <div className="flex items-center gap-2">
                  <div
                    className="w-5 h-5 shrink-0 rounded-sm border border-slate-700"
                    style={{ backgroundColor: `rgb(${closingColor})` }}
                    aria-hidden="true"
                  />
                  <select
                    value={closingColor}
                    onChange={(e) => setClosingColor(e.target.value)}
                    data-testid="vault-pulse-closing-color-select"
                    className={`flex-1 bg-[#121822] border border-slate-800 ${accentFocusBorder} text-white text-xs tracking-wide px-2 py-1.5 outline-none focus:ring-1 ${accentFocusRing} appearance-none cursor-pointer`}
                  >
                    {colorPalette.map((c) => (
                      <option key={c.rgb} value={c.rgb}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Opening color */}
              <div className="mb-1">
                <Label className="text-[11px] tracking-widest uppercase text-slate-400 block mb-1.5">
                  Opening Color
                </Label>
                <div className="flex items-center gap-2">
                  <div
                    className="w-5 h-5 shrink-0 rounded-sm border border-slate-700"
                    style={{ backgroundColor: `rgb(${openingColor})` }}
                    aria-hidden="true"
                  />
                  <select
                    value={openingColor}
                    onChange={(e) => setOpeningColor(e.target.value)}
                    data-testid="vault-pulse-opening-color-select"
                    className={`flex-1 bg-[#121822] border border-slate-800 ${accentFocusBorder} text-white text-xs tracking-wide px-2 py-1.5 outline-none focus:ring-1 ${accentFocusRing} appearance-none cursor-pointer`}
                  >
                    {colorPaletteWithGreen.map((c) => (
                      <option key={c.rgb} value={c.rgb}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

          </div>
        )}
    </dialog>
  );
}
